import React, { useState, createContext, useContext } from 'react';
interface Badge {
  id: string;
  name: string;
  description: string;
  image: string;
  unlocked: boolean;
}
interface Achievement {
  id: string;
  name: string;
  description: string;
  progress: number;
  total: number;
  completed: boolean;
}
interface Stamp {
  id: string;
  cuisine: string;
  restaurant: string;
  date: Date;
}
interface AchievementContextType {
  badges: Badge[];
  achievements: Achievement[];
  stamps: Stamp[];
  points: number;
  level: number;
  addPoints: (points: number) => void;
  unlockBadge: (badgeId: string) => void;
  addStamp: (stamp: Omit<Stamp, 'id' | 'date'>) => void;
  updateAchievementProgress: (achievementId: string, progress: number) => void;
}
const AchievementContext = createContext<AchievementContextType | undefined>(undefined);
export const AchievementProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [badges, setBadges] = useState<Badge[]>([{
    id: 'badge-1',
    name: 'Food Explorer',
    description: 'Try 5 different cuisines',
    image: 'https://example.com/badge1.png',
    unlocked: false
  }, {
    id: 'badge-2',
    name: 'Gourmet Nomad',
    description: 'Order from 10 different restaurants',
    image: 'https://example.com/badge2.png',
    unlocked: false
  }]);
  const [achievements, setAchievements] = useState<Achievement[]>([{
    id: 'achievement-1',
    name: 'Social Butterfly',
    description: 'Complete 5 Mystery Lunches',
    progress: 0,
    total: 5,
    completed: false
  }, {
    id: 'achievement-2',
    name: 'Foodie Photographer',
    description: 'Share 10 food photos',
    progress: 0,
    total: 10,
    completed: false
  }]);
  const [stamps, setStamps] = useState<Stamp[]>([]);
  const [points, setPoints] = useState(0);
  const [level, setLevel] = useState(1);
  const addPoints = (newPoints: number) => {
    setPoints(prev => {
      const total = prev + newPoints;
      const newLevel = Math.floor(total / 1000) + 1;
      if (newLevel > level) {
        setLevel(newLevel);
      }
      return total;
    });
  };
  const unlockBadge = (badgeId: string) => {
    setBadges(prev => prev.map(badge => badge.id === badgeId ? {
      ...badge,
      unlocked: true
    } : badge));
  };
  const addStamp = (stampData: Omit<Stamp, 'id' | 'date'>) => {
    setStamps(prev => [{
      ...stampData,
      id: `stamp-${Date.now()}`,
      date: new Date()
    }, ...prev]);
  };
  const updateAchievementProgress = (achievementId: string, progress: number) => {
    setAchievements(prev => prev.map(achievement => {
      if (achievement.id === achievementId) {
        const newProgress = Math.min(achievement.total, progress);
        return {
          ...achievement,
          progress: newProgress,
          completed: newProgress >= achievement.total
        };
      }
      return achievement;
    }));
  };
  return <AchievementContext.Provider value={{
    badges,
    achievements,
    stamps,
    points,
    level,
    addPoints,
    unlockBadge,
    addStamp,
    updateAchievementProgress
  }} data-id="element-192">
      {children}
    </AchievementContext.Provider>;
};
export const useAchievement = () => {
  const context = useContext(AchievementContext);
  if (context === undefined) {
    throw new Error('useAchievement must be used within an AchievementProvider');
  }
  return context;
};